import { useQuery } from '@tanstack/react-query';
import type { LeaderboardEntry, RankingPlayer } from '@types';
import {
  leaderboardService,
  type LeaderboardMode,
  type LeaderboardPeriod,
  type LeaderboardResponse,
  type LeaderboardRewardsConfig,
} from '@services';

export const LEADERBOARD_QUERY_KEY = ['leaderboard'] as const;

export const useLeaderboard = (mode: LeaderboardMode = 'pve') => {
  const {
    data: players = [],
    isLoading,
    error,
  } = useQuery({
    queryKey: [...LEADERBOARD_QUERY_KEY, mode],
    queryFn: async () => {
      const data = await leaderboardService.getLeaderboard(mode);

      // Transform API data to RankingPlayer format
      const rankedPlayers: RankingPlayer[] = data.map(
        (entry: LeaderboardEntry, index: number) => ({
          id: entry.walletAddress,
          username: '',
          avatar: undefined,
          rank: index + 1,
          score: entry.score,
          badge: undefined,
          time: undefined,
        })
      );

      return rankedPlayers;
    },
    staleTime: 1000 * 60 * 2, // 2 minutes
    gcTime: 1000 * 60 * 5, // 5 minutes
  });

  return {
    players,
    isLoading,
    error,
  };
};

export interface UseLeaderboardWithPeriodResult {
  players: RankingPlayer[];
  isLoading: boolean;
  error: Error | null;
  period: LeaderboardPeriod;
  periodStart: string | null;
  periodEnd: string | null;
  rewards: LeaderboardRewardsConfig | null;
}

export const useLeaderboardWithPeriod = (
  mode: LeaderboardMode = 'pve',
  period: LeaderboardPeriod = 'weekly'
): UseLeaderboardWithPeriodResult => {
  const {
    data,
    isLoading,
    error,
  } = useQuery({
    queryKey: [...LEADERBOARD_QUERY_KEY, mode, period],
    queryFn: async (): Promise<LeaderboardResponse & { rankedPlayers: RankingPlayer[] }> => {
      const response = await leaderboardService.getLeaderboardWithPeriod(mode, period);

      // Transform API data to RankingPlayer format
      const rankedPlayers: RankingPlayer[] = response.entries.map(
        (entry: LeaderboardEntry, index: number) => ({
          id: entry.walletAddress,
          username: '',
          avatar: undefined,
          rank: index + 1,
          score: entry.score,
          badge: undefined,
          time: undefined,
        })
      );

      return {
        ...response,
        rankedPlayers,
      };
    },
    staleTime: 1000 * 60 * 2, // 2 minutes
    gcTime: 1000 * 60 * 5, // 5 minutes
  });

  return {
    players: data?.rankedPlayers ?? [],
    isLoading,
    error: error as Error | null,
    period: data?.period ?? period,
    periodStart: data?.periodStart ?? null,
    periodEnd: data?.periodEnd ?? null,
    rewards: data?.rewards ?? null,
  };
};
